import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { EvaluationTypeEnum } from "../../../../models/EvaluationTypes";
import { LanguageEnum } from "../../../../models/Language";
import { ProgrammingLanguageEnum } from "../../../../models/ProgrammingLanguage";
import { validateUserPermission } from "../../../../services/PermissionGuard";
import translate from "../../../../services/Translate";
import { getAcceptedSubjectExtensions } from "../../../Evaluations/components/CreationModal.function";
import { EvaluationUpdateModalProps } from "./UpdateModal.d";
import { fetchPrograms, updateEvaluation } from "./UpdateModal.function";

const UpdateModal = ({
  show,
  closeModal,
  evaluation,
  language,
  onSuccess,
  permissions,
}: EvaluationUpdateModalProps) => {
  const [loading, setLoading] = useState<{
    edit: boolean;
    programs: boolean;
  }>({
    edit: false,
    programs: false,
  });
  const [programs, setPrograms] = useState<any[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const [labels, setLabels] = useState<{
    owner: string;
    module: string;
    program: string;
  }>({
    owner:
      evaluation?.owner?.full_name ||
      `${evaluation?.owner?.first_name ?? ""} ${evaluation?.owner?.last_name ?? ""}`.trim(),
    module: evaluation.correction_module?.name ?? "",
    program: evaluation.program?.name ?? "",
  });

  const [form, setForm] = useState({
    name: "",
    description: "",
    duration: 60,
    language: "fr-FR",
    programming_language: "",
    type: "",
    is_deprecated: false,
    allow_repass: false,
    owner_id: null as number | null,
    module_id: null as number | null,
    success_ratio: "" as string | number,
    program_id: null as number | null,
  });

  useEffect(() => {
    if (validateUserPermission([{ route: "/programs", methods: ["GET"] }], permissions)) {
      fetchPrograms(setPrograms, (value: boolean) => setLoading((prev) => ({ ...prev, programs: value })), language);
    }
  }, [show, language, permissions]);

  useEffect(() => {
    const defaultType = (evaluation.type ?? "").toLowerCase() as EvaluationTypeEnum;
    const defaultProgrammingLanguage = (evaluation.programming_language ?? "").toLowerCase() as ProgrammingLanguageEnum;

    setForm({
      name: evaluation.name,
      description: evaluation.description,
      duration: evaluation.duration,
      language: evaluation.language,
      programming_language: Object.values(ProgrammingLanguageEnum).includes(defaultProgrammingLanguage)
        ? defaultProgrammingLanguage
        : "other",
      type: Object.values(EvaluationTypeEnum).includes(defaultType) ? defaultType : "html",
      is_deprecated: !!evaluation.is_deprecated,
      allow_repass: !!evaluation.allow_repass,
      owner_id: evaluation.owner?.id ?? null,
      module_id: evaluation.correction_module?.id,
      success_ratio: evaluation.success_ratio,
      program_id: evaluation.program?.id,
    });
    setFile(null);
    setFileInputKey((prev) => prev + 1);
  }, [evaluation]);

  const _handleChange = (key: string, value: any) => {
    if (key === "type" && file) {
      setFile(null);
      setFileInputKey((prev) => prev + 1);
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
  };

  const _clearFile = () => {
    setFile(null);
    setFileInputKey((prev) => prev + 1);
  };

  const _handleSubmit = () => {
    updateEvaluation(
      evaluation.id,
      {
        name: form.name.trim(),
        description: form.description,
        s3_name: file?.name ?? evaluation.s3_name,
        duration: Number(form.duration),
        language: form.language,
        programming_language: form.programming_language.trim(),
        type: form.type.trim(),
        is_deprecated: form.is_deprecated,
        allow_repass: form.allow_repass,
        owner_id: form.owner_id,
        module_id: form.module_id,
        success_ratio: form.success_ratio === "" ? null : Number(form.success_ratio),
        program_id: form.program_id,
      },
      file,
      (value: boolean) => setLoading((prev) => ({ ...prev, edit: value })),
      (updatedEvaluation: any) => {
        onSuccess(updatedEvaluation);
        closeModal();
      },
      language
    );
  };

  return (
    <Modal show={show} onHide={() => closeModal()} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.EVALUATION.UPDATE_MODAL.TITLE")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
            <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "GLOBAL.DESCRIPTION")} />
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(event) => _handleChange("description", event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.S3_NAME")} />
            <Form.Control value={evaluation.s3_name} disabled />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.FILE")}</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                key={fileInputKey}
                type="file"
                accept={getAcceptedSubjectExtensions(form.type)}
                onChange={_handleFileChange}
              />
              {file && (
                <DstButton
                  variant="light"
                  btnClass="text-nowrap"
                  value={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.BUTTONS.REMOVE_FILE")}
                  clickFunction={_clearFile}
                />
              )}
            </div>
            <Form.Text muted>{translate(language, "PAGES.EVALUATION.UPDATE_MODAL.HELPERS.FILE")}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.DURATION")} />
            <Form.Control
              type="number"
              min={1}
              value={form.duration}
              onChange={(event) => _handleChange("duration", Number(event.target.value))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
            </Form.Label>

            <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
              {Object.values(LanguageEnum).map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.PROGRAMMING_LANGUAGE")} />
            </Form.Label>

            <Form.Select
              value={form.programming_language}
              onChange={(event) => _handleChange("programming_language", event.target.value)}
            >
              {Object.values(ProgrammingLanguageEnum).map((programmingLanguage) => (
                <option key={programmingLanguage} value={programmingLanguage}>
                  {programmingLanguage}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.TYPE")} />
            </Form.Label>

            <Form.Select value={form.type} onChange={(event) => _handleChange("type", event.target.value)}>
              {Object.values(EvaluationTypeEnum).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.OWNER")} />
            <MultiSelectLiveDropdown
              category={["users"]}
              language={language}
              isSingleSelect
              toggleClass={`w-100 border ${!form.owner_id ? "text-muted" : ""}`}
              label={labels.owner || translate(language, "PAGES.EVALUATION.UPDATE_MODAL.PLACEHOLDERS.SELECT_OWNER")}
              initialSelectedItems={
                evaluation?.owner
                  ? [
                      {
                        category: "users",
                        value: {
                          ...evaluation.owner,
                          first_name: evaluation.owner.first_name ?? evaluation.owner.full_name?.split(" ")[0] ?? "",
                          last_name:
                            evaluation.owner.last_name ??
                            evaluation.owner.full_name?.split(" ").slice(1).join(" ") ??
                            "",
                        },
                      },
                    ]
                  : []
              }
              onConfirm={(selected) => {
                const user = selected?.[0]?.value ?? null;
                setLabels((prev) => ({
                  ...prev,
                  owner: user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() : "",
                }));
                _handleChange("owner_id", user?.id ?? null);
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.CORRECTION_MODULE")}</Form.Label>
            <MultiSelectLiveDropdown
              category={["modules"]}
              language={language}
              isSingleSelect
              toggleClass={`w-100 border ${!form.module_id ? "text-muted" : ""}`}
              label={
                labels.module ||
                translate(language, "PAGES.EVALUATION.UPDATE_MODAL.PLACEHOLDERS.SELECT_CORRECTION_MODULE")
              }
              initialSelectedItems={
                evaluation?.correction_module ? [{ category: "modules", value: evaluation.correction_module }] : []
              }
              onConfirm={(selected) => {
                const module = selected?.[0]?.value ?? null;
                setLabels((prev) => ({ ...prev, module: module?.name ?? "" }));
                _handleChange("module_id", module?.id ?? null);
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.PROGRAM")}</Form.Label>
            <MultiSelectDropdown
              items={programs.map((program) => ({ id: program.id, name: program.name }))}
              language={language}
              label={labels.program || translate(language, "PAGES.EVALUATION.UPDATE_MODAL.PLACEHOLDERS.SELECT_PROGRAM")}
              mock={loading.programs}
              isSingleSelect
              isSingleSelectRemovable
              badgeItemProperty="name"
              displayItem={(item: any) => <span>{item.name}</span>}
              onConfirm={([item]: any[]) => {
                setLabels((prev) => ({ ...prev, program: item?.name ?? "" }));
                _handleChange("program_id", item?.id ?? null);
              }}
              defaultSelectedItems={programs
                .map((program) => ({ id: program.id, name: program.name }))
                .filter((program) => program.id === form.program_id)}
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.SUCCESS_RATIO")}</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={form.success_ratio}
              placeholder={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.PLACEHOLDERS.SUCCESS_RATIO")}
              onChange={(event) => _handleChange("success_ratio", event.target.value)}
            />
            <Form.Text muted>{translate(language, "PAGES.EVALUATION.UPDATE_MODAL.HELPERS.SUCCESS_RATIO")}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="evaluation-is-deprecated"
              label={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.IS_DEPRECATED")}
              checked={form.is_deprecated}
              onChange={(event) => _handleChange("is_deprecated", event.target.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="evaluation-allow-repass"
              label={translate(language, "PAGES.EVALUATION.UPDATE_MODAL.FIELDS.ALLOW_REPASS")}
              checked={form.allow_repass}
              onChange={(event) => _handleChange("allow_repass", event.target.checked)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <DstButton variant="secondary" value={translate(language, "GLOBAL.CANCEL")} clickFunction={closeModal} />
        <DstButton
          loading={loading.edit}
          value={translate(language, "GLOBAL.SAVE")}
          clickFunction={_handleSubmit}
          disabled={
            loading.edit ||
            !form.name.trim() ||
            !form.language ||
            !form.programming_language.trim() ||
            !form.type.trim() ||
            !form.owner_id ||
            Number(form.success_ratio) < 0 ||
            Number(form.success_ratio) > 1
          }
        />
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateModal;
